import { useEffect, useState } from 'react';
import API from '../api';
import OrgStructureModal from './OrgStructureModal';
import DisclosureScopeModal from './DisclosureScopeModal';
import { logDownload } from '../utils/eventLog';

// 관리자/생산관리자/협력사 같은 "역할(role)" 개념은 더 이상 없습니다. 계정은 최고관리자
// (superadmin) 또는 일반 계정(member) 둘 중 하나이며, 화면 표시는 최고관리자 여부 + 회원구분
// (member_type: staff/partner)만으로 구분합니다. 모든 실제 접근권한은 아래 카드의
// "🔐 권한설정" 버튼(계정관리 > 권한설정)에서 개별적으로 부여합니다.
const ROLE_LABEL = { superadmin:'최고관리자', staff:'사내임직원', partner:'협력사', individual:'개인관리' };
const ROLE_COLOR = { superadmin:'#993556', staff:'#185FA5', partner:'#854F0B', individual:'#0D9488' };
const ROLE_BG    = { superadmin:'#FBEAF0', staff:'#E6F1FB', partner:'#FAEEDA', individual:'#CCFBF1' };

const FIELD_LABEL = {
  name: '이름', position: '직급', company: '회사명', address: '주소',
  phone: '연락처', email: '이메일', emergency_phone: '긴급연락처'
};

// ── 회원구분(사내임직원/협력사/개인관리) ─────────────────────────
// 최고관리자는 항상 "사내임직원"으로만 표시(변경 불가), 그 외 계정은 DB의 member_type
// 컬럼(staff/partner/individual) 값을 그대로 사용합니다. 회원가입 화면의 구분과 동일한 개념입니다.
// "개인관리"는 공개범위 설정 화면의 회원구분 조건(사내임직원/협력사/개인관리) 중 하나로 매칭되는
// 용도로 사용합니다(예: 개인 사업자, 특정 1인 거래처 등 — 부서/직급 없이 개인 단위로 관리할 회원).
const MEMBER_TYPE_OPTIONS = [
  { value: 'staff',      label: '사내임직원' },
  { value: 'partner',    label: '협력사' },
  { value: 'individual', label: '개인관리' },
];
const toMemberType = (u) => {
  if (u?.member_type === 'partner') return 'partner';
  if (u?.member_type === 'individual') return 'individual';
  return 'staff';
};
// 카드 배지/아바타 색상 조회용 — 최고관리자는 'superadmin' 키, 그 외는 회원구분 키를 사용
const roleDisplayKey = (u) => (u.role === 'superadmin' ? 'superadmin' : toMemberType(u));

// "2026-08-05 11:00:00" 같은 형식을 "2026.08.05"로 표기 (값이 없으면 '-')
const formatDate = (v) => {
  if (!v) return '-';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}.${m[2]}.${m[3]}` : String(v);
};

// ── 권한설정(계정관리에 통합) ─────────────────────────
// 기존 권한설정 메뉴(Permissions.js)에서 쓰던 일반설정/세부설정 모달을 그대로 가져와서
// 계정 카드의 "🔐 권한설정" 버튼으로 바로 열 수 있게 합니다.
// accounting(회계관리)는 2026-08 메뉴 개편으로 추가된 준비중 placeholder라 아직 쓸 수 있는
// 기능(=쓰기 동작)이 없다 — display와 같은 이유로 쓰기 권한 토글을 숨긴다.
// assets(자산관리)는 2026-08-29 실제 기능(자산등록/수정/삭제/이력관리 등)이 구현되어 더 이상
// placeholder가 아니므로 NO_WRITE_MENUS에서 제외한다(다른 실사용 메뉴와 동일하게 쓰기권한을
// 관리자가 사용자별로 켜고 끌 수 있어야 함).
// home(홈)은 2026-08부터 예외다 — 홈 화면 자체엔 쓰기 동작이 없지만, 이 쓰기권한을
// "사이드바 메뉴편집(드래그앤드롭 재구성) 기능 사용 가능 여부"로 재활용하기로 했으므로
// NO_WRITE_MENUS에서 빼서 관리자가 사용자별로 켜고 끌 수 있게 한다(App.js Sidebar 참고).
// accounting(회계관리)는 2026-09 실제 기능(계정과목/거래처/통장/매출매입/전표 등)이 구현되어
// 더 이상 placeholder가 아니므로 NO_WRITE_MENUS에서 제외한다(assets와 동일한 사유).
const NO_WRITE_MENUS = ['display'];

const DETAIL_DEFS = {
  notices: {
    read:  [{ key:'comment_allowed',    label:'댓글 허용' }],
    write: [
      { key:'display_expose',   label:'전광판 노출 가능여부 설정' },
      { key:'delete_allowed',   label:'삭제가능 여부설정' },
    ],
  },
  tasks: {
    read:  [
      { key:'download_allowed',   label:'첨부파일 다운로드 가능여부 설정' },
      { key:'minutes_download',   label:'회의록 다운로드' },
      { key:'request_read_all',   label:'업무요청 전체 조회 (끄면 내가 요청했거나 요청받은 내역만 조회)' },
    ],
    write: [
      // 2026-08: '업무등록 가능'은 폐지 — 쓰기권한이 있으면 누구나 등록 가능하도록 변경되어
      // 더 이상 별도로 켜고 끌 필요가 없습니다.
      { key:'delete_allowed',        label:'목록삭제 가능' },
      { key:'status_change_allowed', label:'상태변경' },
      { key:'minutes_write_allowed', label:'회의록 작성' },
      { key:'research_note_write_allowed', label:'연구노트 작성 (개발/지원 탭)' },
      { key:'request_write_allowed', label:'업무요청 등록' },
    ],
  },
  certs: {
    read:  [{ key:'download_allowed',   label:'다운로드 가능여부 설정' }],
    write: [{ key:'delete_allowed',     label:'삭제가능 여부설정' }],
  },
  contracts: {
    read:  [{ key:'download_allowed',   label:'다운로드 가능여부 설정' }],
    write: [{ key:'delete_allowed',     label:'삭제가능 여부설정' }],
  },
  assets: {
    read: [
      { key:'download_allowed',   label:'내보내기/라벨출력 가능여부 설정' },
      { key:'depreciation_read',  label:'감가상각 조회' },
      { key:'audit_read',         label:'재물조사 조회' },
    ],
    write: [
      { key:'category_manage',    label:'기준정보(품목/위치/거래처) 관리' },
      { key:'delete_allowed',     label:'삭제가능 여부설정' },
      { key:'depreciation_write', label:'감가상각 계정과목 관리' },
      { key:'audit_write',        label:'재물조사 등록/처리(스캔 포함)' },
    ],
  },
  // 회계관리 — 2026-09 신규(경리나라 등 참조 구현: 계정과목/거래처/통장/매출·매입/전표).
  accounting: {
    read: [],
    write: [
      { key:'account_manage',     label:'계정과목 관리' },
      { key:'partner_manage',     label:'거래처원장 관리' },
      { key:'bank_manage',        label:'통장/카드 등록 관리' },
      { key:'transaction_manage', label:'계좌 거래내역 등록/수정' },
      { key:'voucher_manage',     label:'전표(분개) 등록/수정' },
      { key:'po_manage',          label:'발주서 등록/수정/상태변경' },
      { key:'inbound_doc_manage', label:'수신문서(거래문서함) 등록' },
      { key:'delete_allowed',     label:'삭제가능 여부설정' },
    ],
  },
  'sales-activities': {
    read:  [{ key:'detail_view_allowed', label:'영업관리 상세보기' }],
    write: [
      { key:'register_allowed',    label:'영업관리 등록' },
      { key:'update_allowed',      label:'영업관리 업데이트' },
      { key:'list_delete_allowed', label:'목록 삭제가능' },
      { key:'quotation_register_allowed', label:'견적서 등록' },
      { key:'quotation_update_allowed',   label:'견적서 수정' },
      { key:'quotation_delete_allowed',   label:'견적서 삭제' },
    ],
  },
  sales: {
    read:  [],
    write: [
      { key:'list_delete_allowed',      label:'납품출고 삭제' },
      { key:'transfer_register_allowed', label:'공장-본사 이관출고 등록' },
      { key:'transfer_edit_allowed',     label:'공장-본사 이관출고 수정' },
      { key:'transfer_delete_allowed',   label:'공장-본사 이관출고 삭제' },
      { key:'status_hold_allowed',      label:'출고보류 승인' },
      { key:'status_pending_allowed',   label:'출고대기 승인' },
      { key:'status_complete_allowed',  label:'출고완료 승인' },
    ],
  },
  productions: {
    // ※ 읽기(=버튼/목록이 보이는지)와 쓰기(=실제로 등록·승인·삭제할 수 있는지)는 서로 다른
    // 별개의 설정입니다. 승인 버튼을 사용하게 하려면 같은 단계의 읽기·쓰기 항목을 함께 켜야 합니다
    // (예: 생산승인 버튼을 쓰게 하려면 "생산계획 생산승인 버튼"과 "생산승인" 둘 다 켜야 함).
    read: [
      { key:'plan_list_visible',           label:'생산계획 목록보기' },
      { key:'status_list_visible',         label:'생산현황 목록보기' },
      { key:'status_detail_visible',       label:'생산현황 상세보기' },
      { key:'admin_approval_visible',      label:'생산계획 관리승인 버튼' },
      { key:'production_approval_visible', label:'생산계획 생산승인 버튼' },
    ],
    write: [
      { key:'admin_approval_action',      label:'관리승인' },
      { key:'production_approval_action', label:'생산승인' },
      { key:'plan_register_allowed',      label:'생산계획 등록' },
      { key:'list_delete_allowed',        label:'생산계획 삭제' },
      // 생산현황 삭제요청(등록자 본인 + 이 권한 → 최고관리자 승인 필요)과 생산현황 삭제
      // (최고관리자이거나 이 권한 → 승인 없이 즉시삭제)는 서로 다른 별개의 권한입니다.
      { key:'status_delete_allowed',      label:'생산현황 삭제요청' },
      { key:'status_delete_action',       label:'생산현황 삭제' },
    ],
  },
  inventory: {
    read: [
      { key:'finished_goods_read', label:'완제품 목록보기' },
      { key:'parts_read',          label:'부품 목록보기' },
      { key:'consumables_read',    label:'소모품 목록보기' },
      { key:'history_read',        label:'수정이력 조회' },
      { key:'material_order_read', label:'원자재 목록보기' },
    ],
    write: [
      { key:'finished_goods_write',  label:'완제품 재고현황 수정/삭제' },
      { key:'defect_register',       label:'완제품 불량처리 수정/삭제' },
      { key:'parts_write',           label:'부품 추가/수정/삭제' },
      { key:'consumables_write',     label:'소모품 추가/수정/삭제' },
      { key:'product_register',      label:'제품등록' },
      { key:'history_delete',        label:'수정이력 삭제' },
      { key:'material_order_write',  label:'원자재 등록/수정/삭제' },
    ],
  },
  storage: {
    shared: [{ key:'accessible_folders', label:'접근가능 폴더를 지정', type:'folders' }],
  },
  'hr-management': {
    read:  [{ key:'payroll_read', label:'급여 전체 조회 (끄면 본인 급여만 조회)' }],
    write: [
      { key:'employee_write',    label:'사원정보 등록/수정' },
      { key:'attendance_write',  label:'근태관리 등록/수정' },
      { key:'leave_approve',     label:'휴가 승인/반려 + 연차 부여' },
      { key:'appointment_write', label:'인사발령 등록' },
      { key:'payroll_write',     label:'급여 등록/수정' },
      { key:'recruit_write',     label:'채용공고/지원자 등록' },
      { key:'evaluation_write',  label:'평가 등록/수정' },
      { key:'training_write',    label:'교육 등록/참가자 관리' },
      { key:'event_write',       label:'경조사 등록' },
      { key:'delete_allowed',    label:'각 목록 삭제 가능' },
    ],
  },
  'e-approval': {
    read:  [],
    write: [
      { key:'form_manage',       label:'문서양식 관리' },
      { key:'delegation_manage', label:'다른 사람의 결재 위임 관리' },
    ],
  },
  'company-management': {
    read:  [{ key:'download_allowed', label:'다운로드 가능여부 설정' }],
    write: [{ key:'delete_allowed',   label:'삭제가능 여부설정' }],
  },
  // 연락처관리(명함) — 2026-09 신규. company-management와 동일한 상세권한 구성.
  'contacts': {
    read:  [{ key:'download_allowed', label:'다운로드 가능여부 설정' }],
    write: [{ key:'delete_allowed',   label:'삭제가능 여부설정' }],
  },
};

export default function AdminUsers() {
	// 엑셀 다운로드 (토큰 포함)
	const downloadExcel = async () => {
	  try {
		const token = sessionStorage.getItem('token');
		const response = await fetch(
		  API.defaults.baseURL.replace('/api', '') + '/api/admin/users/export/excel',
		  {
			headers: { Authorization: 'Bearer ' + token }
		  }
		);

		if (!response.ok) {
		  const err = await response.json();
		  alert(err.message || '다운로드 실패');
		  return;
		}

		// 파일 다운로드 처리
		const blob = await response.blob();
		const url  = window.URL.createObjectURL(blob);
		const a    = document.createElement('a');
		a.href     = url;
		a.download = 'IMS_Members.csv';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
		logDownload('계정관리', 'IMS_Members.csv');
	  } catch(err) {
		alert('다운로드 중 오류가 발생했습니다.');
	  }
	};

  const [users, setUsers]               = useState([]);
  const [editRequests, setEditRequests] = useState([]);
  const [msg, setMsg]                   = useState('');
  const [msgType, setMsgType]           = useState('success');

  // 승인된 계정 목록 - 사내임직원/협력사/개인관리 탭 + 검색
  const [memberTab, setMemberTab] = useState('staff'); // 'staff' | 'partner' | 'individual'
  const [search, setSearch]       = useState('');

  // 조직구성(부서/직급) 관리 모달 + 부서/직급 마스터 목록(계정별 배정용 선택 목록)
  const [showOrgModal, setShowOrgModal] = useState(false);
  // 공개범위 설정 모달
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [departments, setDepartments]   = useState([]);
  const [positions, setPositions]       = useState([]);
  const loadOrg = () => API.get('/org').then(r => {
    setDepartments(r.data.departments || []);
    setPositions(r.data.positions || []);
  }).catch(() => {});

  // 권한설정(일반설정/세부설정) — 계정 카드의 "🔐 권한설정" 버튼용
  const [permMenus, setPermMenus]             = useState([]);
  const [permRows, setPermRows]               = useState([]);
  const [permDetailRows, setPermDetailRows]   = useState([]);
  const [permUser, setPermUser]               = useState(null); // 일반설정 대상 계정
  const [detailUser, setDetailUser]           = useState(null); // 세부설정 대상 계정(일반설정 위에 겹쳐 표시)
  const [detailMenuKey, setDetailMenuKey]     = useState(null); // 상세설정을 누른 메뉴(자동 선택)
  const [detailLiveOverride, setDetailLiveOverride] = useState(null); // 일반설정에서 아직 저장 전인 읽기/쓰기 값

  // 접속제한(로그인 5회 실패로 잠긴 계정) — 배지 클릭 시 사유 확인 + 해제(비밀번호 재설정) 모달
  const [lockUser, setLockUser] = useState(null);
  const loadPerms = () => API.get('/permissions').then(r => {
    setPermMenus(r.data.menus || []);
    setPermRows(r.data.permissions || []);
    setPermDetailRows(r.data.details || []);
  }).catch(() => {});

  const showMsg = (text, type = 'success') => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(''), 3000);
  };

  // 사용자 목록 불러오기 — 실패하면 화면이 그냥 빈 상태로 보여서 원인을 알 수 없었기 때문에,
  // 조회 실패 시에도 서버가 준 오류 메시지를 화면에 표시합니다.
  const load = () => API.get('/admin/users').then(r => setUsers(r.data))
    .catch(err => showMsg(err.response?.data?.message || `계정 목록을 불러오지 못했습니다. (${err.response?.status || '네트워크 오류'})`, 'error'));

  // 수정 요청 목록 불러오기
  const loadEditRequests = () =>
    API.get('/admin/edit-requests').then(r => setEditRequests(r.data)).catch(() => {});

  useEffect(() => { load(); loadEditRequests(); loadOrg(); loadPerms(); }, []);

  // API 호출 실패 시 화면에 아무 반응이 없어 원인을 알 수 없던 문제를 막기 위해, 모든 계정관리
  // 액션은 이 헬퍼로 감싸서 실패하면 서버가 준 오류 메시지를 alert로 바로 보여줍니다.
  const runAction = async (fn) => {
    try {
      await fn();
    } catch (err) {
      alert(err.response?.data?.message || `요청 중 오류가 발생했습니다. (${err.response?.status || '네트워크 오류'})`);
    }
  };

  // 계정 승인
  const approve = (id) => runAction(async () => {
    await API.patch(`/admin/users/${id}/approve`);
    showMsg('승인 완료!');
    load();
  });

  // 승인 취소
  const reject = (id) => runAction(async () => {
    await API.patch(`/admin/users/${id}/reject`);
    showMsg('승인 취소!');
    load();
  });

  // 계정 삭제 — 아바타(이름 첫글자 원) 클릭 시 실행. 승인취소(대기 상태로 되돌리는 것)와 달리
  // 계정 자체를 완전히 삭제하며 되돌릴 수 없으므로, 경고 문구로 한 번 더 확인받는다.
  const deleteAccount = (u) => {
    if (u.role === 'superadmin') return;
    if (!window.confirm(`"${u.name}"(@${u.username}) 계정을 삭제하시겠습니까?\n삭제된 계정은 복구할 수 없습니다.`)) return;
    runAction(async () => {
      await API.delete(`/admin/users/${u.id}`);
      showMsg('계정이 삭제되었습니다.');
      load();
    });
  };

  // 회원구분 변경 (사내임직원 ↔ 협력사) — role이 아니라 member_type 컬럼만 바뀝니다
  // (역할 개념이 없어졌으므로 이 변경은 실제 접근권한에는 영향을 주지 않습니다. 접근권한은
  // 별도로 "🔐 권한설정" 버튼에서 개별 부여/조정합니다.)
  const [memberTypeDrafts, setMemberTypeDrafts] = useState({});
  const changeMemberType = (id, memberType) => runAction(async () => {
    await API.patch(`/admin/users/${id}/member-type`, { memberType });
    showMsg('회원구분이 변경되었습니다!');
    setMemberTypeDrafts(prev => { const n = { ...prev }; delete n[id]; return n; });
    load();
  });

  // 부서/직급 변경 (조직구성에서 등록한 목록 중 선택 — 변경버튼 누르기 전까지는 임시 선택값만 저장)
  const [deptDrafts, setDeptDrafts] = useState({});
  const [posDrafts, setPosDrafts]   = useState({});
  const changeDepartment = (id, department) => runAction(async () => {
    await API.patch(`/admin/users/${id}/department`, { department });
    showMsg('부서가 변경되었습니다!');
    setDeptDrafts(prev => { const n = { ...prev }; delete n[id]; return n; });
    load();
  });
  const changePosition = (id, position) => runAction(async () => {
    await API.patch(`/admin/users/${id}/position`, { position });
    showMsg('직급이 변경되었습니다!');
    setPosDrafts(prev => { const n = { ...prev }; delete n[id]; return n; });
    load();
  });

  // 수정 요청 승인
  const approveEdit = (id) => runAction(async () => {
    await API.patch(`/admin/edit-requests/${id}/approve`);
    showMsg('수정 승인 완료!');
    loadEditRequests();
    load();
  });

  // 수정 요청 거부
  const rejectEdit = (id) => runAction(async () => {
    await API.patch(`/admin/edit-requests/${id}/reject`);
    showMsg('수정 거부 완료!', 'error');
    loadEditRequests();
  });

  const pending     = users.filter(u => u.approved === 0);
  const approvedAll = users.filter(u => u.approved === 1);
  const approved = approvedAll
    .filter(u => toMemberType(u) === memberTab)
    .filter(u => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [u.name, u.username, u.company, u.department, u.position]
        .some(v => (v || '').toLowerCase().includes(q));
    });

  const tabBtn = (key, label, count) => (
    <button onClick={() => setMemberTab(key)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        background: 'none', border: 'none', padding: '9px 2px 11px', marginRight: 28,
        borderBottom: memberTab === key ? '2.5px solid #185FA5' : '2.5px solid transparent',
        cursor: 'pointer', fontSize: 15, fontWeight: memberTab === key ? 700 : 500,
        color: memberTab === key ? '#185FA5' : '#888'
      }}>
      {label}
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 20, height: 20, borderRadius: 10, padding: '0 6px',
        fontSize: 12, fontWeight: 700,
        background: memberTab === key ? '#185FA5' : '#e5e7eb',
        color: memberTab === key ? '#fff' : '#888'
      }}>
        {count}
      </span>
    </button>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1690 }}>

      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>👤 계정 관리</h2>
          <p style={{ fontSize: 13, color: '#888' }}>회원가입 신청을 승인하거나 역할을 변경할 수 있습니다.</p>
        </div>
        {/* 조직구성 / 엑셀 다운로드 버튼 */}
        <div className="hdr-actions" style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowOrgModal(true)}
            style={{ background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            🏢 조직구성
          </button>
          <button
            onClick={() => setShowDisclosureModal(true)}
            style={{ background: '#0F6E56', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            🎯 공개범위 설정
          </button>
          <button
            onClick={downloadExcel}
            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            📥 회원정보 다운로드
          </button>
        </div>
      </div>

      {/* 메시지 */}
      {msg && (
        <div style={{ background: msgType === 'success' ? '#d1fae5' : '#fee2e2',
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          color: msgType === 'success' ? '#065f46' : '#dc2626' }}>
          {msg}
        </div>
      )}

      {/* ── 수정 요청 목록 ── */}
      {editRequests.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ ...sectionLabel, color: '#d97706' }}>
            ✏️ 정보 수정 요청 ({editRequests.length}건) — 승인 대기 중
          </div>
          {editRequests.map(r => (
            <div key={r.id}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 10,
                padding: '12px 16px', marginBottom: 8, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{r.user_name}</span>
                <span style={{ color: '#888', margin: '0 6px' }}>(@{r.username})</span>
                <span style={{ color: '#aaa' }}>→</span>
                <span style={{ margin: '0 6px', background: '#E6F1FB', color: '#185FA5',
                  padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 500 }}>
                  {FIELD_LABEL[r.field_name] || r.field_name}
                </span>
                <span style={{ color: '#aaa' }}>변경 요청:</span>
                <span style={{ margin: '0 6px', fontWeight: 600, color: '#1a1a1a' }}>{r.new_value}</span>
                {r.old_value && (
                  <span style={{ fontSize: 11, color: '#aaa' }}>(기존: {r.old_value})</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#aaa' }}>{r.created_at?.slice(0, 10)}</span>
                <button style={btnStyle('#185FA5')} onClick={() => approveEdit(r.id)}>✅ 승인</button>
                <button style={btnStyle('#dc2626')} onClick={() => rejectEdit(r.id)}>❌ 거부</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 승인 대기 계정 ── */}
      {pending.length > 0 && (
        <>
          <div style={sectionLabel}>⏳ 승인 대기 ({pending.length}명)</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:8 }}>
            {pending.map(u => (
              <div key={u.id} style={cardStyle('#fffbeb', '#fef3c7')}>
                <div style={userInfo}>
                  <div style={avatar('#854F0B', '#FAEEDA')}>{u.name[0]}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{u.position && <span style={{ fontWeight:500, color:'#555' }}>{u.position} </span>}{u.name}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      @{u.username} · {ROLE_LABEL[roleDisplayKey(u)]} · {u.created_at?.slice(0, 10)}
                    </div>
                    {u.company && <div style={{ fontSize: 12, color: '#aaa' }}>🏢 {u.company}</div>}
                    {u.phone   && <div style={{ fontSize: 12, color: '#aaa' }}>📞 {u.phone}</div>}
                    {u.email   && <div style={{ fontSize: 12, color: '#aaa' }}>✉️ {u.email}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnStyle('#185FA5')} onClick={() => approve(u.id)}>✅ 승인</button>
                  <button style={btnStyle('#dc2626')} onClick={() => reject(u.id)}>❌ 거부</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── 승인된 계정: 사내임직원/협력사 탭 + 검색 ── */}
      {pending.length > 0 && (
        <div style={{ ...sectionLabel, color: '#d97706' }}>
          ⏳ 승인 대기 중인 회원이 있습니다 ({pending.length}명)
        </div>
      )}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16,
        gap:12, flexWrap:'wrap', borderBottom:'1.5px solid #eee' }}>
        <div style={{ display:'flex' }}>
          {tabBtn('staff', '사내임직원', approvedAll.filter(u => toMemberType(u) === 'staff').length)}
          {tabBtn('partner', '협력사', approvedAll.filter(u => toMemberType(u) === 'partner').length)}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="이름 / 아이디 / 회사명 검색"
          style={{ padding:'8px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:13,
            outline:'none', width:240, marginBottom:8 }} />
      </div>

      {approved.length === 0 && (
        <div style={{ fontSize: 13, color: '#aaa', textAlign: 'center', padding: '30px 0' }}>
          해당하는 계정이 없습니다.
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        {approved.map(u => (
          <div key={u.id} style={cardStyleCol('#fff', '#eee')}>
            {/* 상단: 계정 정보 + 권한설정 버튼 */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
              <div style={userInfo}>
                {/* 아바타(이름 첫글자 원) 클릭 → 계정삭제. 최고관리자 계정은 클릭해도 동작하지 않음(deleteAccount 내부 가드) */}
                <div
                  onClick={() => deleteAccount(u)}
                  title={u.role === 'superadmin' ? undefined : '클릭하여 계정 삭제'}
                  style={{ ...avatar(ROLE_COLOR[roleDisplayKey(u)], ROLE_BG[roleDisplayKey(u)]),
                    cursor: u.role === 'superadmin' ? 'default' : 'pointer' }}>
                  {u.name[0]}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{u.position && <span style={{ fontWeight:500, color:'#555' }}>{u.position} </span>}{u.name}</span>
                    <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 20,
                      background: ROLE_BG[roleDisplayKey(u)], color: ROLE_COLOR[roleDisplayKey(u)], fontWeight: 500 }}>
                      {u.department || '부서 미지정'}
                    </span>
                    {!!u.locked && (
                      <button onClick={() => setLockUser(u)}
                        style={{ fontSize: 12, padding: '2px 8px', borderRadius: 20, border: 'none',
                          background: '#FEE2E2', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}>
                        🔒 접속제한
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>@{u.username}</div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                    {u.company && <span style={{ fontSize: 12, color: '#888' }}>🏢 {u.company}</span>}
                    {u.phone   && <span style={{ fontSize: 12, color: '#888' }}>📞 {u.phone}</span>}
                    {u.email   && <span style={{ fontSize: 12, color: '#888' }}>✉️ {u.email}</span>}
                    {u.emergency_phone && <span style={{ fontSize: 12, color: '#888' }}>🚨 {u.emergency_phone}</span>}
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                <div style={{ fontSize:11, color:'#aaa', textAlign:'right', lineHeight:1.6, whiteSpace:'nowrap' }}>
                  <div>가입일 : {formatDate(u.created_at)}</div>
                  <div>수정일 : {formatDate(u.updated_at)}</div>
                </div>
                {u.role !== 'superadmin' && (
                  <button onClick={() => setPermUser(u)}
                    style={{ background:'#fff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:8,
                      padding:'7px 14px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
                      flexShrink:0 }}>
                    🔐 권한설정
                  </button>
                )}
              </div>
            </div>

            {/* 하단: 회원구분 / 부서 / 직급 / 승인취소 */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', rowGap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              {/* 회원구분 — 사내임직원 ↔ 협력사 (최고관리자는 "사내임직원"으로 고정 표시만, 변경 불가) */}
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <select value={u.role === 'superadmin' ? 'staff' : (memberTypeDrafts[u.id] ?? toMemberType(u))}
                  disabled={u.role === 'superadmin'}
                  onChange={e => setMemberTypeDrafts(prev => ({ ...prev, [u.id]: e.target.value }))}
                  style={{ fontSize:13, padding:'7px 10px', border:'1px solid #ddd', borderRadius:6,
                    cursor: u.role === 'superadmin' ? 'not-allowed' : 'pointer', width:130,
                    background: u.role === 'superadmin' ? '#f5f5f5' : '#fff', color: u.role === 'superadmin' ? '#999' : '#1a1a1a' }}>
                  {MEMBER_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button
                  style={btnStyle(u.role !== 'superadmin' && (memberTypeDrafts[u.id] ?? toMemberType(u)) !== toMemberType(u) ? '#0F6E56' : '#aaa', 'small')}
                  disabled={u.role === 'superadmin' || (memberTypeDrafts[u.id] ?? toMemberType(u)) === toMemberType(u)}
                  onClick={() => changeMemberType(u.id, memberTypeDrafts[u.id] ?? toMemberType(u))}>
                  회원구분변경
                </button>
              </div>

              {/* 부서 배정 — 조직구성에서 등록한 목록 중 선택(협력사는 가입 시 입력한 담당부서가 기본값으로 미리 선택됨) / 최고관리자도 변경 가능 */}
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <select value={deptDrafts[u.id] ?? (u.department || '')} onChange={e => setDeptDrafts(prev => ({ ...prev, [u.id]: e.target.value }))}
                  style={{ fontSize:13, padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, cursor:'pointer', width:150 }}>
                  <option value="">부서 미지정</option>
                  {u.department && !departments.some(d => d.name === u.department) && (
                    <option value={u.department}>{u.department} (목록 외)</option>
                  )}
                  {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
                <button
                  style={btnStyle((deptDrafts[u.id] ?? (u.department || '')) !== (u.department || '') ? '#0F6E56' : '#aaa', 'small')}
                  disabled={(deptDrafts[u.id] ?? (u.department || '')) === (u.department || '')}
                  onClick={() => changeDepartment(u.id, deptDrafts[u.id] ?? (u.department || ''))}>
                  부서변경
                </button>
              </div>

              {/* 직급 배정 — 조직구성에서 등록한 목록 중 선택 / 최고관리자도 변경 가능 */}
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <select value={posDrafts[u.id] ?? (u.position || '')} onChange={e => setPosDrafts(prev => ({ ...prev, [u.id]: e.target.value }))}
                  style={{ fontSize:13, padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, cursor:'pointer', width:150 }}>
                  <option value="">직급 미지정</option>
                  {u.position && !positions.some(p => p.name === u.position) && (
                    <option value={u.position}>{u.position} (목록 외)</option>
                  )}
                  {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
                <button
                  style={btnStyle((posDrafts[u.id] ?? (u.position || '')) !== (u.position || '') ? '#0F6E56' : '#aaa', 'small')}
                  disabled={(posDrafts[u.id] ?? (u.position || '')) === (u.position || '')}
                  onClick={() => changePosition(u.id, posDrafts[u.id] ?? (u.position || ''))}>
                  직급변경
                </button>
              </div>

              <button
                style={{ ...btnStyle(u.role === 'superadmin' ? '#ccc' : '#dc2626', 'small'), padding:'7px 14px',
                  cursor: u.role === 'superadmin' ? 'not-allowed' : 'pointer' }}
                disabled={u.role === 'superadmin'}
                onClick={() => reject(u.id)}>
                승인취소
              </button>
            </div>
          </div>
        ))}
      </div>

      {showOrgModal && (
        <OrgStructureModal onClose={() => { setShowOrgModal(false); loadOrg(); }} />
      )}

      {showDisclosureModal && (
        <DisclosureScopeModal onClose={() => setShowDisclosureModal(false)} />
      )}

      {permUser && (
        <PermissionModal
          user={permUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === permUser.username)}
          onClose={() => setPermUser(null)}
          onSaved={() => { setPermUser(null); showMsg('권한이 저장되었습니다.'); loadPerms(); }}
          onOpenDetail={(menuKey, liveGranted) => {
            setDetailUser(permUser);
            setDetailMenuKey(menuKey);
            setDetailLiveOverride({ menu_key: menuKey, ...liveGranted });
          }}
        />
      )}

      {detailUser && (
        <DetailPermissionModal
          user={detailUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === detailUser.username)}
          initialDetails={permDetailRows.filter(d => d.username === detailUser.username)}
          initialMenu={detailMenuKey}
          liveOverride={detailLiveOverride}
          onClose={() => { setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null); }}
          onSaved={() => {
            setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null);
            showMsg('상세설정이 저장되었습니다.'); loadPerms();
          }}
        />
      )}

      {lockUser && (
        <LockModal
          user={lockUser}
          onClose={() => setLockUser(null)}
          onDone={() => { setLockUser(null); showMsg('접속제한이 해제되었습니다.'); load(); }}
        />
      )}
    </div>
  );
}

const sectionLabel = { fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 10, marginTop: 20 };
const cardStyle = (bg, border) => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: bg, border: `1px solid ${border}`, borderRadius: 10,
  padding: '12px 16px', marginBottom: 8, flexWrap: 'wrap', gap: 10
});
// 승인된 계정 카드 — 상단(정보+권한설정)/하단(배정 액션) 2단 구성
const cardStyleCol = (bg, border) => ({
  background: bg, border: `1px solid ${border}`, borderRadius: 10,
  padding: '14px 16px', marginBottom: 0
});
const userInfo = { display: 'flex', alignItems: 'center', gap: 10 };
const avatar = (color, bg) => ({
  width: 36, height: 36, borderRadius: '50%', background: bg, color,
  fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0
});
const btnStyle = (bg, size) => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 6,
  padding: size === 'small' ? '5px 10px' : '7px 14px',
  fontSize: size === 'small' ? 12 : 13, cursor: 'pointer', fontWeight: 500
});

const thStyle = { padding:'12px 16px', textAlign:'center', fontSize:13, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb', whiteSpace:'nowrap' };
const tdStyle = { padding:'12px 16px', fontSize:14, borderBottom:'1px solid #f0f0f0', textAlign:'center' };
const quickBtn = {
  padding:'6px 12px', fontSize:12, fontWeight:600, color:'#555',
  background:'#f8fafc', border:'1px solid #ddd', borderRadius:6, cursor:'pointer'
};

// 권한설정 모달 헤더용 — 회사명 ㅣ 부서명 ㅣ 직급 ㅣ 이름 (아이디) 형식으로 표시
const formatUserLine = (u) => {
  const parts = [u.company, u.department, u.position, u.name].filter(Boolean);
  return `${parts.join(' ㅣ ')} (${u.username})`;
};

// ── 접속제한 모달: 비밀번호 5회 오류로 잠긴 계정의 사유 확인 + 해제(새 비밀번호 설정) ─────
// 해제는 반드시 새 비밀번호를 함께 설정해야 하며(요청사항), 해제되면 실패 카운트도 초기화된다.
function LockModal({ user, onClose, onDone }) {
  const [pw, setPw]           = useState('');
  const [pw2, setPw2]         = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const unlock = async () => {
    if (pw.length < 6) { setError('새 비밀번호는 6자 이상이어야 합니다.'); return; }
    if (pw !== pw2) { setError('비밀번호가 서로 일치하지 않습니다.'); return; }
    setError('');
    setSaving(true);
    try {
      await API.patch(`/admin/users/${user.id}/unlock`, { newPassword: pw });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || `해제 중 오류가 발생했습니다. (${err.response?.status || '네트워크 오류'})`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, width:420, maxWidth:'90vw', padding:'24px 24px 20px' }}>
        <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>🔒 접속제한 해제</h3>
        <p style={{ margin:'10px 0 0', fontSize:13, color:'#555' }}>
          {formatUserLine(user)}
        </p>
        <div style={{ marginTop:14, background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8,
          padding:'10px 12px', fontSize:13, color:'#991B1B' }}>
          <div><strong>제한사유</strong> : {user.lock_reason || '비밀번호 5회 오류'}</div>
          <div style={{ marginTop:2 }}><strong>제한시각</strong> : {user.locked_at || '-'}</div>
        </div>
        <p style={{ margin:'14px 0 6px', fontSize:13, color:'#555' }}>
          해제하려면 이 계정의 새 비밀번호를 설정해주세요. 설정한 비밀번호로 재로그인할 수 있습니다.
        </p>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)}
          placeholder="새 비밀번호 (6자 이상)"
          style={{ width:'100%', boxSizing:'border-box', padding:'9px 12px', border:'1px solid #ddd',
            borderRadius:8, fontSize:13, marginBottom:8, outline:'none' }} />
        <input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
          placeholder="새 비밀번호 확인"
          style={{ width:'100%', boxSizing:'border-box', padding:'9px 12px', border:'1px solid #ddd',
            borderRadius:8, fontSize:13, outline:'none' }} />
        {error && <div style={{ marginTop:8, fontSize:12, color:'#dc2626' }}>{error}</div>}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:20 }}>
          <button onClick={onClose}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            취소
          </button>
          <button onClick={unlock} disabled={saving}
            style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.6 : 1 }}>
            {saving ? '처리 중...' : '해제 및 비밀번호 설정'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 일반설정: 사용자별로 메뉴 단위 읽기/쓰기 권한을 설정하는 모달 ─────
// (기존 권한설정 메뉴의 PermissionModal 그대로 + 헤더에 "상세설정" 버튼 추가)
function PermissionModal({ user, menus, initialPerms, onClose, onSaved, onOpenDetail }) {
  const initial = {};
  menus.forEach(m => {
    const found = initialPerms.find(p => p.menu_key === m.key);
    initial[m.key] = { can_read: !!found?.can_read, can_write: !!found?.can_write };
  });

  const [perms, setPerms]     = useState(initial);
  const [saving, setSaving]   = useState(false);

  const toggle = (key, field) =>
    setPerms(prev => ({ ...prev, [key]: { ...prev[key], [field]: !prev[key][field] } }));

  const setAll = (field, value) =>
    setPerms(prev => {
      const next = {};
      menus.forEach(m => {
        if (field === 'can_write' && NO_WRITE_MENUS.includes(m.key)) {
          next[m.key] = { ...prev[m.key], can_write: false };
        } else {
          next[m.key] = { ...prev[m.key], [field]: value };
        }
      });
      return next;
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = menus.map(m => ({
        menu_key: m.key,
        can_read: perms[m.key].can_read,
        can_write: perms[m.key].can_write,
      }));
      await API.put('/permissions/' + user.username, { permissions: payload });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:520, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🔐 권한설정</h3>
            <div style={{ fontSize:13, color:'#888', marginTop:4 }}>{formatUserLine(user)}</div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ padding:'16px 24px' }}>
          {/* 일괄 설정 */}
          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', justifyContent:'center' }}>
            <button onClick={() => setAll('can_read', true)}  style={quickBtn}>읽기 전체 켜기</button>
            <button onClick={() => setAll('can_read', false)} style={quickBtn}>읽기 전체 끄기</button>
            <button onClick={() => setAll('can_write', true)}  style={quickBtn}>쓰기 전체 켜기</button>
            <button onClick={() => setAll('can_write', false)} style={quickBtn}>쓰기 전체 끄기</button>
          </div>

          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                <th style={{ ...thStyle, padding:'8px 10px' }}>메뉴</th>
                <th style={{ ...thStyle, padding:'8px 10px', textAlign:'center', width:60 }}>읽기</th>
                <th style={{ ...thStyle, padding:'8px 10px', textAlign:'center', width:60 }}>쓰기</th>
                <th style={{ ...thStyle, padding:'8px 10px', textAlign:'center', width:96 }}>상세설정</th>
              </tr>
            </thead>
            <tbody>
              {menus.map(m => {
                const hasDetail = !!DETAIL_DEFS[m.key];
                const granted = perms[m.key].can_read || perms[m.key].can_write;
                return (
                <tr key={m.key}>
                  <td style={{ ...tdStyle, padding:'8px 10px' }}>
                    {m.label}
                    {m.key === 'home' && (
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>쓰기 = 사이드바 메뉴편집 허용</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, padding:'8px 10px', textAlign:'center' }}>
                    <input type="checkbox" checked={perms[m.key].can_read}
                      onChange={() => toggle(m.key, 'can_read')}
                      style={{ width:16, height:16, cursor:'pointer' }} />
                  </td>
                  <td style={{ ...tdStyle, padding:'8px 10px', textAlign:'center' }}>
                    {NO_WRITE_MENUS.includes(m.key) ? (
                      <span style={{ color:'#ccc' }}>-</span>
                    ) : (
                      <input type="checkbox" checked={perms[m.key].can_write}
                        onChange={() => toggle(m.key, 'can_write')}
                        style={{ width:16, height:16, cursor:'pointer' }} />
                    )}
                  </td>
                  <td style={{ ...tdStyle, padding:'8px 10px', textAlign:'center' }}>
                    {!hasDetail ? (
                      <span style={{ color:'#ccc' }}>-</span>
                    ) : (
                      <button
                        disabled={!granted}
                        onClick={() => onOpenDetail(m.key, perms[m.key])}
                        title={!granted ? '먼저 읽기 또는 쓰기를 켜주세요' : ''}
                        style={{
                          padding:'4px 10px', fontSize:12, fontWeight:600, borderRadius:6,
                          cursor: granted ? 'pointer' : 'not-allowed',
                          background: granted ? '#fff' : '#f5f5f5',
                          color: granted ? '#7C3AED' : '#bbb',
                          border: `1px solid ${granted ? '#7C3AED' : '#ddd'}`
                        }}>
                        상세설정
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:1, padding:'11px', background:saving?'#aaa':'#185FA5', color:'#fff',
              border:'none', borderRadius:8, cursor:saving?'not-allowed':'pointer', fontSize:14, fontWeight:600 }}>
            {saving ? '저장 중...' : '저장'}
          </button>
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', background:'#f0f0f0', color:'#555',
              border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 세부설정: 일반설정(읽기/쓰기)이 켜져 있는 범위 안에서만 세부항목을 켜고 끄는 모달 ─────
// (기존 권한설정 메뉴의 DetailPermissionModal 그대로 — 일반설정 위에 겹쳐서 표시되므로 zIndex만 더 높임)
function DetailPermissionModal({ user, menus, initialPerms, initialDetails, initialMenu, liveOverride, onClose, onSaved }) {
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [values, setValues]             = useState({});
  const [folderList, setFolderList]     = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [saving, setSaving]             = useState(false);

  // 특정 메뉴의 "상세설정" 버튼을 눌러서 들어온 경우, 해당 메뉴를 자동으로 선택해서 바로 보여줌
  useEffect(() => {
    if (initialMenu) selectMenu(initialMenu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMenu]);

  const getGranted = (menuKey) => {
    // 일반설정에서 방금 켰지만 아직 저장 전인 값은 liveOverride로 먼저 반영
    if (liveOverride && liveOverride.menu_key === menuKey) {
      return { canRead: !!liveOverride.can_read, canWrite: !!liveOverride.can_write };
    }
    const p = (initialPerms || []).find(x => x.menu_key === menuKey);
    return { canRead: !!p?.can_read, canWrite: !!p?.can_write };
  };

  const selectMenu = (menuKey) => {
    setSelectedMenu(menuKey || null);
    if (!menuKey) { setValues({}); return; }
    const rows = (initialDetails || []).filter(d => d.menu_key === menuKey);
    const next = {};
    rows.forEach(r => {
      if (r.detail_key === 'accessible_folders') {
        try { next[r.detail_key] = JSON.parse(r.value || '[]'); } catch { next[r.detail_key] = []; }
      } else {
        next[r.detail_key] = r.value === '1';
      }
    });
    setValues(next);

    if (menuKey === 'storage' && folderList.length === 0) {
      setFolderLoading(true);
      API.get('/storage/list')
        .then(r => setFolderList((r.data.items || []).filter(it => it.isDir).map(it => it.name)))
        .catch(() => {})
        .finally(() => setFolderLoading(false));
    }
  };

  const toggleBool = (key) => setValues(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleFolder = (key, name) => setValues(prev => {
    const cur = Array.isArray(prev[key]) ? prev[key] : [];
    const next = cur.includes(name) ? cur.filter(n => n !== name) : [...cur, name];
    return { ...prev, [key]: next };
  });

  const handleSave = async () => {
    if (!selectedMenu) return;
    setSaving(true);
    try {
      const def = DETAIL_DEFS[selectedMenu];
      const keys = def.shared ? def.shared.map(d => d.key) : [...def.read.map(d => d.key), ...def.write.map(d => d.key)];
      const details = {};
      keys.forEach(k => { details[k] = values[k] ?? (Array.isArray(values[k]) ? [] : false); });
      await API.put('/permissions/' + user.username + '/detail', { menu_key: selectedMenu, details });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const def = selectedMenu ? DETAIL_DEFS[selectedMenu] : null;
  const { canRead, canWrite } = selectedMenu ? getGranted(selectedMenu) : { canRead:false, canWrite:false };

  const renderSection = (title, granted, items) => (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:13, fontWeight:600, color:'#555', marginBottom:8 }}>{title}</div>
      {!granted ? (
        <div style={{ fontSize:12, color:'#d97706', background:'#fffbeb', border:'1px solid #fef3c7',
          borderRadius:8, padding:'10px 12px' }}>
          먼저 권한설정에서 해당 권한을 켜야 상세설정을 사용할 수 있습니다.
        </div>
      ) : items.length === 0 ? (
        <div style={{ fontSize:12, color:'#aaa', padding:'4px 0' }}>이 항목에는 별도 상세설정이 없습니다.</div>
      ) : (
        items.map(it => (
          <label key={it.key} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', fontSize:14, cursor:'pointer' }}>
            <input type="checkbox" checked={!!values[it.key]} onChange={() => toggleBool(it.key)}
              style={{ width:16, height:16, cursor:'pointer' }} />
            {it.label}
          </label>
        ))
      )}
    </div>
  );

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2050 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:560, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🧩 상세설정</h3>
            <div style={{ fontSize:13, color:'#888', marginTop:4 }}>{formatUserLine(user)}</div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ padding:'16px 24px' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#185FA5', marginBottom:16 }}>
            {menus.find(m => m.key === selectedMenu)?.label || selectedMenu}
          </div>

          {selectedMenu && def?.shared && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#555', marginBottom:8 }}>
                {def.shared[0].label} <span style={{ color:'#aaa', fontWeight:400 }}>(읽기·쓰기 공통)</span>
              </div>
              {!canRead && !canWrite ? (
                <div style={{ fontSize:12, color:'#d97706', background:'#fffbeb', border:'1px solid #fef3c7',
                  borderRadius:8, padding:'10px 12px' }}>
                  먼저 권한설정에서 읽기 또는 쓰기 권한을 켜야 상세설정을 사용할 수 있습니다.
                </div>
              ) : folderLoading ? (
                <div style={{ fontSize:12, color:'#aaa' }}>폴더 목록을 불러오는 중...</div>
              ) : folderList.length === 0 ? (
                <div style={{ fontSize:12, color:'#aaa' }}>스토리지에 생성된 폴더가 없습니다.</div>
              ) : (
                <div style={{ border:'1px solid #eee', borderRadius:8, padding:'8px 12px', maxHeight:200, overflowY:'auto' }}>
                  {folderList.map(name => (
                    <label key={name} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', fontSize:14, cursor:'pointer' }}>
                      <input type="checkbox"
                        checked={(values.accessible_folders || []).includes(name)}
                        onChange={() => toggleFolder('accessible_folders', name)}
                        style={{ width:16, height:16, cursor:'pointer' }} />
                      📁 {name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {selectedMenu && !def?.shared && (
            <>
              {renderSection('읽기 상세설정', canRead, def.read)}
              {renderSection('쓰기 상세설정', canWrite, def.write)}
            </>
          )}
        </div>

        <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
          {selectedMenu && (
            <button onClick={handleSave} disabled={saving}
              style={{ flex:1, padding:'11px', background:saving?'#aaa':'#7C3AED', color:'#fff',
                border:'none', borderRadius:8, cursor:saving?'not-allowed':'pointer', fontSize:14, fontWeight:600 }}>
              {saving ? '저장 중...' : '저장'}
            </button>
          )}
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', background:'#f0f0f0', color:'#555',
              border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            {selectedMenu ? '취소' : '닫기'}
          </button>
        </div>
      </div>
    </div>
  );
}